/**
 * MER-222: the row-level drill's query plan.
 *
 * WHAT THE DRILL PROMISES. A reader clicks a tile and the modal says "these
 * are the rows behind that number". That promise only holds if the drill
 * selects the same population the tile aggregated. Every predicate the tile
 * applied has to be applied again, in the same order, or the rows on screen
 * are a different measurement wearing the tile's name.
 *
 * WHAT WAS WRONG. The modal ran `.select("*")` and applied three of the five
 * predicates the KPI executor applies. Two were missing:
 *
 *   1. `resolveLatestFilters` was never called, so a widget pinning
 *      `{op: latest}` (six per tenant today, all on the Safety and Fleet tab)
 *      reached `applyFilters` with an unresolved clause and threw. The reader
 *      clicked a live tile and got a developer sentence about PostgREST.
 *   2. `applyFeedQuality` was never called, so a ratio tile that excluded the
 *      periods the pipeline stamped FEED_INCOMPLETE drilled into rows that
 *      included them. The table could not add up to the tile by construction.
 *
 * WHAT THIS MODULE OWNS. The column plan: which columns the drill asks the
 * database for. Not a table's whole row, and not a guess at what a reader
 * might like. A widget declares its own binding (its measure, its filters,
 * its timeline, its chart axes, its table columns) and the drill selects
 * exactly that, plus the small set of identity columns that make a detail row
 * readable rather than a bare number.
 *
 * WHY THAT IS A SECURITY CHANGE AND NOT A PERFORMANCE ONE. `select("*")`
 * moves every column of the row across the wire, including the plumbing the
 * presentation layer then drops on the client. Dropping a column after it has
 * been sent is a display decision, not a containment one. The acceptance
 * criterion on MER-222 is that no internal column appears in a drill RESULT,
 * and the result is the response, not the rendering. Narrowing the projection
 * is the only way to satisfy it.
 *
 * WHAT THIS MODULE DOES NOT DO. It does not scope the drill to a tenant. That
 * is scopeTenantTable's job at the call site, on top of the RLS policy, and
 * nothing here can widen it: a projection names columns and never rows.
 *
 * Plain .mjs so node --test drives it with no bundler (the priorWindow.mjs /
 * drillTarget.mjs precedent); drillQuery.d.mts types it for the modal.
 */

/**
 * Internal plumbing that must never reach a customer, on the wire or on the
 * screen. tablePresentation.ts imports this as HIDDEN_COLUMNS and drops it at
 * render as a backstop; the drill's projection drops it before the request is
 * sent.
 *
 * This list lived in tablePresentation.ts with a hand-copied duplicate in
 * table-presentation.test.mjs under a "keep in sync" comment. Two copies of a
 * containment list is one copy too many, so it lives here and both read it.
 */
export const INTERNAL_COLUMNS = Object.freeze([
  "tenant_id",
  "period_type",
  "period_end",
  "created_date",
  "last_updated_date",
  "created_at",
  "updated_at",
  "synced_at",
  "_synced_at",
  "id",
  // Internal QA flags. Their "SUPPRESSED" / "PARTIAL_DATA" values used to
  // render as customer-visible cell text (MER-438 addendum).
  "data_quality",
  "n_suppressed",
  "n_periods",
]);

const INTERNAL_SET = new Set(INTERNAL_COLUMNS);

/** Is this an internal plumbing column? */
export function isInternalColumn(column) {
  return INTERNAL_SET.has(String(column));
}

/** The modal's row cap. One page of detail, not an export. */
export const DRILL_ROW_CAP = 500;

/**
 * Identity columns per canonical relation: who, where, when and what kind.
 *
 * These are what turn a column of numbers into rows a reader recognises. They
 * are deliberately few and deliberately boring, and every one of them is a
 * core column that predates this ticket, so naming them cannot break a drill
 * against a schema that is one migration behind.
 *
 * Two of them earn their place twice over. The period columns are what
 * presentTable sorts on (PERIOD_KEYS), and equipment_type / line_of_business
 * are what it uses to drop the "unknown" noise rows (EQUIPMENT_KEYS). A
 * projection that omitted them would silently disable both.
 *
 * A relation absent from this map (the CQT views) contributes no identity
 * columns; the plan is then the widget's own binding alone, which for those
 * widgets is a declared column allow-list.
 */
export const DRILL_IDENTITY_COLUMNS = Object.freeze({
  kpi_summary: ["period_date", "period_start", "division", "equipment_type"],
  service_event: [
    "event_date",
    "completion_date",
    "event_id",
    "customer_id",
    "division",
    "equipment_type",
    "service_type",
    "work_type",
    "route_id",
    "completion_status",
    "posted_status",
  ],
  financial_health: ["period_date", "period_start", "division"],
  route_kpis: ["period_date", "period", "division", "equipment_type", "route_id"],
  asset: ["period_start", "division", "asset_id", "vehicle_name", "asset_type"],
  customer: [
    "customer_id",
    "customer_name",
    "division",
    "customer_type",
    "account_class",
    "equipment_type_assigned",
  ],
  ar_current: [
    "import_date",
    "account_id",
    "account_name",
    "division_name",
    "billgroup_name",
    "account_status_text",
  ],
  ar_transactions: [
    "date",
    "account_id",
    "account_name",
    "division_name",
    "bill_group_name",
    "ar_type",
  ],
  top_ar_accounts: [
    "import_date",
    "account_id",
    "account_name",
    "division",
    "bill_group",
    "status",
  ],
  site_contracts: ["site_id", "site_name", "division_name", "expiration_date", "expiration_bucket"],
  contract_expirations: [
    "contract_id",
    "site_id",
    "site_name",
    "division",
    "contract_type",
    "expiration_date",
    "expiration_bucket",
  ],
  audit_finding: ["finding_id", "category", "title", "severity", "last_seen_date"],
  insight: ["insight_id", "insight_type", "title", "severity", "division", "equipment_type"],
});

/* ------------------------------------------------------------------ */
/* Reading a widget's own binding                                      */
/* ------------------------------------------------------------------ */

/**
 * A bare column name: letters, digits and underscores, nothing else.
 *
 * The projection is assembled from manifest strings, and a PostgREST select
 * list is not only a list of columns. `customer(name)` is an EMBEDDED
 * RESOURCE: it joins another relation into the response, and the tenant bind
 * this module's caller applies (`.eq("tenant_id", ...)`) constrains the outer
 * relation only. A hand-authored or overridden manifest naming one would turn
 * a projection into a join, which is the one way a column list could widen
 * what a request returns. `*`, `:` (aliasing), `::` (casts), `->` (JSON paths)
 * and whitespace are refused for the same reason: a projection should be
 * boring, and anything here that is not boring did not come from the schema.
 *
 * No manifest names one today. This is a guard, not a fix.
 */
const BARE_COLUMN = /^[A-Za-z_][A-Za-z0-9_]*$/;

function push(out, value) {
  if (typeof value !== "string") return;
  const name = value.trim();
  if (name === "" || isInternalColumn(name)) return;
  if (!BARE_COLUMN.test(name)) return;
  out.push(name);
}

/**
 * Every column named inside a measure expression, e.g.
 * "sum(ar_91_120) + sum(ar_over_120)" yields both.
 *
 * Deliberately a scan for aggregate arguments rather than a parse: this is a
 * projection, so a name too many costs a column of detail and a name too few
 * costs the reader the figure they clicked. measureExpression.ts owns the real
 * parse for the values themselves.
 */
export function expressionColumns(expression) {
  if (typeof expression !== "string") return [];
  const out = [];
  const re = /\b(?:sum|avg|count|min|max)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)/gi;
  let m;
  while ((m = re.exec(expression)) !== null) push(out, m[1]);
  return out;
}

function paneColumns(pane, out) {
  if (!pane || typeof pane !== "object") return;
  push(out, pane.column);
  for (const col of expressionColumns(pane.expression)) push(out, col);
}

/**
 * The columns a widget's own declaration names: its measure, its filters, its
 * timeline anchor, its chart axes, its table allow-list, its net / ratio
 * overlays.
 *
 * This is the "widget-scoped" half of the ticket title. It is derived from the
 * manifest rather than from a stored list of each table's columns, which means
 * it cannot drift away from the schema: every column here is one the tile
 * itself already reads, so a column the database does not have would have
 * broken the tile long before it reached the drill.
 */
export function widgetBoundColumns(widget) {
  const out = [];
  if (!widget || typeof widget !== "object") return out;

  const field = widget.field;
  if (field && typeof field === "object") {
    push(out, field.column);
    for (const col of expressionColumns(field.expression)) push(out, col);
    paneColumns(field.primary, out);
    paneColumns(field.secondary, out);
    paneColumns(field.tertiary, out);
  }

  push(out, widget.x_axis);
  for (const series of widget.y_axis_series ?? []) paneColumns(series, out);
  push(out, widget.slice_by);
  paneColumns(widget.value, out);
  push(out, widget.rows);

  if (Array.isArray(widget.columns)) for (const col of widget.columns) push(out, col);
  for (const measure of widget.measures ?? []) paneColumns(measure, out);

  for (const clause of [
    widget.filter,
    widget.net?.add,
    widget.net?.subtract,
    widget.ratio?.numerator,
    widget.ratio?.denominator,
  ]) {
    if (clause && typeof clause === "object") for (const key of Object.keys(clause)) push(out, key);
  }

  push(out, widget.timeline?.column);
  return out;
}

/* ------------------------------------------------------------------ */
/* The plan                                                            */
/* ------------------------------------------------------------------ */

/**
 * The ordered, de-duplicated column list the drill asks for, or null when the
 * widget names nothing at all.
 *
 * Order is identity first, binding second, so the reader's eye lands on the
 * account or the route before the measure. Null is the caller's signal to fall
 * back to the wide read; it happens only for a widget with no declared
 * binding, which cannot render a tile either.
 *
 * `extra` carries the columns a query path needs that the manifest does not
 * name: a derived ratio's numerator and denominator, a weighted ratio's
 * weight, and the feed-quality column when the drill has to reproduce the
 * tile's FEED_INCOMPLETE exclusion.
 */
export function drillColumns(widget, table, extra = []) {
  const identity = DRILL_IDENTITY_COLUMNS[String(table)] ?? [];
  const named = [];
  for (const col of identity) push(named, col);
  for (const col of widgetBoundColumns(widget)) push(named, col);
  for (const col of extra) push(named, col);
  const seen = new Set();
  const plan = [];
  for (const col of named) {
    if (seen.has(col)) continue;
    seen.add(col);
    plan.push(col);
  }
  return plan.length === 0 ? null : plan;
}

/** The PostgREST select string for a plan, or "*" when there is no plan. */
export function drillSelect(widget, table, extra = []) {
  const plan = drillColumns(widget, table, extra);
  return plan === null ? "*" : plan.join(",");
}
